import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import misumiAmericasLogoUrl from '../assets/brand/misumi-americas-logo.png'
import { FormAlert } from '../components/auth/FormAlert'
import { FormField } from '../components/auth/FormField'
import { useToast } from '../components/ui/ToastProvider'
import { apiClient } from '../lib/api/client'
import { loginProblemCopy } from '../lib/auth/problem-messages'
import { safeReturnTo } from '../lib/auth/return-to'
import { setAuthenticated, setOnboardingRequired } from '../lib/auth/session-store'

/**
 * Customer-facing credential login (F0.3, PR-AUTH-01).
 *
 * UID-301 (design-fidelity pass): rebuilt to match the design prototype
 * (`Fictiv Platform.dc.html:187–209`) — a single-step email + password card
 * ("Welcome back"), a "Remember me" / "Forgot password" row, a primary
 * "Log in" button, an "or" divider, and a "Log in with SSO" fallback, with the
 * brand mark switched to MISUMI AMERICAS. This replaces the earlier
 * progressive two-step (email → password) screen; `/auth/login` takes both
 * fields at once, so a single form is fully functional.
 *
 * CONTRACT GAPS (accepted, UI shipped ahead of backend — same convention as
 * the SSO notes below): the API exposes no password-reset endpoint and
 * `/auth/login` takes no "remember me" flag, so:
 *  - "Forgot password" only shows a toast for now (no self-serve reset flow /
 *    endpoint exists yet).
 *  - "Remember me" is captured but not yet forwarded (session lifetime is
 *    server-controlled by the BFF); wiring it needs a contract change.
 * Both are flagged to the EM/PM as follow-ups.
 *
 * SSO: "Log in with SSO" is a stub for now — it does NOT navigate anywhere; it
 * only surfaces a "Redirecting to SSO…" toast, because the OIDC hand-off isn't
 * wired up on this screen yet (contract/flow follow-up). The separate
 * enterprise "sign in with your company" entry was removed from this screen
 * (UID-301).
 */
export function LoginPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') ?? undefined

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [emailError, setEmailError] = useState<string | undefined>()
  const [passwordError, setPasswordError] = useState<string | undefined>()
  const [serverError, setServerError] = useState<{ title: string; detail: string } | undefined>()
  const [submitting, setSubmitting] = useState(false)

  const alertRef = useRef<HTMLDivElement | null>(null)
  const emailInputRef = useRef<HTMLInputElement | null>(null)
  const passwordInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (serverError) {
      alertRef.current?.focus()
    }
  }, [serverError])

  // USP-42 / WCAG 2.2 AA error identification: a client-side validation error
  // moves focus to the offending field and is announced, matching the
  // server-error path above.
  useEffect(() => {
    if (emailError) {
      emailInputRef.current?.focus()
    }
  }, [emailError])

  useEffect(() => {
    if (passwordError) {
      passwordInputRef.current?.focus()
    }
  }, [passwordError])

  function signupHref(): string {
    return returnTo ? `/signup?returnTo=${encodeURIComponent(returnTo)}` : '/signup'
  }

  async function handleSubmit() {
    // Validate top-down so focus lands on the first offending field (the
    // effects above move focus when an error is set).
    if (!email.trim()) {
      setEmailError('Enter your email address.')
      return
    }
    if (!password) {
      setEmailError(undefined)
      setPasswordError('Password is required.')
      return
    }
    setEmailError(undefined)
    setPasswordError(undefined)
    setServerError(undefined)
    setSubmitting(true)
    try {
      const { data, error, response } = await apiClient.POST('/auth/login', {
        body: { email: email.trim(), password },
      })
      if (data) {
        setAuthenticated(data)
        navigate(safeReturnTo(returnTo, '/'), { replace: true })
        return
      }
      // F0.5 (S0.8): a verified user with no membership yet gets 409
      // `onboarding_required`. The BFF still establishes a real (account-less)
      // session on this response and delivers its CSRF token in the
      // `csrf-token` HEADER (not the JSON body, which is a Problem here) — see
      // `session-store.ts`'s `onboarding_required` state doc comment. Hand
      // that token to the store and continue into the create-company wizard
      // rather than dead-ending on a generic error.
      if (error?.type === 'onboarding_required') {
        const csrfToken = response.headers.get('csrf-token')
        if (csrfToken) {
          setOnboardingRequired(csrfToken)
          navigate('/onboarding/company', { state: { email: email.trim(), returnTo } })
          return
        }
        // Defensive fallback only — a live BFF always sets this header on this
        // response. Falls through to the generic error path below.
      }
      const copy = loginProblemCopy(error?.type, error?.detail)
      setServerError(copy)
    } catch {
      setServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="login-split">
      <div className="login-split-form-col">
        <section className="auth-page login-form-panel">
          <img src={misumiAmericasLogoUrl} alt="MISUMI AMERICAS" className="login-brand-mark" />
          <h1 className="display-heading login-heading">Welcome back</h1>
          <p className="login-subtitle">
            Need to create an account? <Link to={signupHref()}>Sign up</Link>
          </p>
          {serverError && (
            <FormAlert ref={alertRef} title={serverError.title} detail={serverError.detail} />
          )}
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              void handleSubmit()
            }}
          >
            <FormField
              ref={emailInputRef}
              id="login-email"
              label="Email address"
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              error={emailError}
              required
            />
            <FormField
              ref={passwordInputRef}
              id="login-password"
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              error={passwordError}
              required
            />
            <div className="login-remember-row">
              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                />
                Remember me
              </label>
              {/* Stub: no self-serve password-reset flow exists yet (no
                  contract endpoint — see this component's CONTRACT GAPS), so
                  this only surfaces a toast rather than navigating or opening a
                  mail client. */}
              <button
                type="button"
                className="login-forgot"
                onClick={() =>
                  showToast('Password reset is coming soon — contact support@misumi.com', 'info')
                }
              >
                Forgot password
              </button>
            </div>
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-lg login-submit login-primary"
                disabled={submitting}
              >
                {submitting ? 'Signing in…' : 'Log in'}
              </button>
            </div>
          </form>
          <div className="login-divider">
            <span>or</span>
          </div>
          {/* Stub: SSO hand-off isn't wired on this screen yet, so this only
              signals intent via a toast — it does NOT navigate. */}
          <button
            type="button"
            className="btn btn-outline btn-lg login-submit login-sso"
            onClick={() => showToast('Redirecting to SSO…', 'info')}
          >
            Log in with SSO
          </button>
        </section>
      </div>
      {/* Full-bleed brand hero (screens.md §1). The marketing image carries its
          own copy, so no text is overlaid here. `aria-hidden` — decorative. */}
      <div className="login-brand-panel" aria-hidden="true" />
    </div>
  )
}
